/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../../components/navigable_breadcrumb_link/styles";
import {
  RestaurantGroupContainer,
  StyledCreateButton,
  StyledTitle,
  StyledTitleContainer
} from "../restaurant_groups/styles";
import {
  Box,
  Breadcrumbs,
  Divider,
  FormControl,
  Select,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip
} from "@mui/material";
import { StyledBox } from "../reports/global_employee_search/styles";
import { StyledSubTitle } from "../employee_groups/group_members/styles";
import AddIcon from '@mui/icons-material/Add';
import { StyledInputLabel } from "../network_guests/styles";
import DropdownSearchBar from "../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { StyledMenuItem } from "../../components/styled_menu_item";
import { SelectedMenuItem } from "../../components/selected_menu_item";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { Query_Keys } from "../../lib/queryKeys";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useNotificationContext } from "../../contexts/notificationContext";
import { AbilityContext } from "../../contexts/abilityContext";
import { strictCanScope } from "../../utils/caslStrict";
import { FilterContainer } from "../../components/filter_container";
import { UpdateButton } from "../../components/update_button";
import { selectBaseStyles, viewTypographyStyles } from "../reports/styles";
import camelize from "../../utils/camelize";
import { IndexTable } from "../../components/index_table";
import { Loader } from "../../components/loader";
import { hasSingleItem } from "../../utils/hasSingleItem";
import { getSingleItemValue } from "../../utils/getSingleItemValue";
import ClearIcon from '@mui/icons-material/Clear';
import { ResetButton } from "../../components/reset_button";
import moment from "moment";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../utils/checkAdmin";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { DiscountsSection } from "./discounts_section";
import { CalendarSection } from "./calendar_section";
import { EventFormDrawer } from "./event_form_drawer";
import { buildQueryParams } from "../../utils/buildQueryParams";
import { formatDynamicDiscountScopeLabel } from "./scope_labels";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { dynamicDiscountMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/dynamicDiscountMutationKeys";
import { useDebounce } from "../../utils/useDebounce";
import { resetPageAfterDeletion } from '../../utils/resetPageAfterDeletion'
import { PerPage } from "../../constants/perPage";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import {
  getDropdownNextPageParam,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../utils/dropdownInfiniteQuery";

const ALL_VALUE = 'All';
const drawerWidth = 470
const createEmptyTimeSlot = () => ({ start_time: '', end_time: '', all_day: false })
const allDayDisplayTimeRange = {
  start_time: '12:00 AM',
  end_time: '11:59 PM',
}
const allDayRequestTimeRange = {
  start_time: '00:00',
  end_time: '23:59',
}
const dynamicDiscountSections = [
  { key: 'events', label: 'Events' },
  { key: 'discounts', label: 'Perqs' },
  { key: 'calendar', label: 'Calendar' },
]
const dynamicDiscountModeOptions = [
  { value: 'additive', label: 'Additive' },
  { value: 'override', label: 'Override' },
]
const scopeFilterOptions = [
  { value: ALL_VALUE, label: 'All' },
  { value: 'group', label: 'Restaurant Group Level' },
  { value: 'restaurant', label: 'Restaurant Level' },
]
const defaultFilters = {
  restaurant_group_id: [ALL_VALUE],
  scope_type: ALL_VALUE,
}

const timeOptions = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2)
  const minutes = index % 2 === 0 ? 0 : 30

  return dayjs().hour(hours).minute(minutes).format('hh:mm A')
})

const formatTimeForForm = (value) => {
  if (!value) {
    return ''
  }

  const parsedTime = moment(value, ['HH:mm', 'hh:mm A'], true)
  return parsedTime.isValid() ? parsedTime.format('hh:mm A') : value
}

const formatTimeForRequest = (value) => {
  if (!value) {
    return null
  }

  const parsedTime = moment(value, ['hh:mm A', 'HH:mm'], true)
  return parsedTime.isValid() ? parsedTime.format('HH:mm') : value
}

const formatEventScopeLabel = (scopeType) => formatDynamicDiscountScopeLabel(scopeType, {
  groupLabel: 'Restaurant Group Level',
  restaurantLabel: 'Restaurant Level',
  fallbackLabel: 'Restaurant Level',
})

const flattenDropdownPages = (data) => data.pages.flat()

const mergeOptionsIfChanged = (currentOptions = [], nextOptions = [], key, comparedFields = []) => {
  const mergedOptions = mergeUniqueByKey(currentOptions, nextOptions, key)

  if (mergedOptions.length !== currentOptions.length) {
    return mergedOptions
  }

  const hasChanges = mergedOptions.some((option, index) => {
    const currentOption = currentOptions[index]

    if (currentOption?.[key] !== option?.[key]) {
      return true
    }

    return comparedFields.some((field) => currentOption?.[field] !== option?.[field])
  })

  return hasChanges ? mergedOptions : currentOptions
}

const buildEventPayload = (eventData, { includeRestaurantGroup = false, allowRestaurantClear = false } = {}) => {
  const payload = {
    name: eventData?.name,
    start_date: eventData?.start_date,
    end_date: eventData?.end_date || eventData?.start_date,
    time_slots: (eventData?.time_slots || []).map((timeSlot) => ({
      ...(timeSlot?.id ? { id: timeSlot.id } : {}),
      all_day: !!timeSlot?.all_day,
      start_time: timeSlot?.all_day ? allDayRequestTimeRange.start_time : formatTimeForRequest(timeSlot?.start_time),
      end_time: timeSlot?.all_day ? allDayRequestTimeRange.end_time : formatTimeForRequest(timeSlot?.end_time),
    }))
  }

  if (eventData?.restaurant_id) {
    payload.restaurant_id = eventData.restaurant_id
    payload.dynamic_discount_event_mode = eventData?.dynamic_discount_event_mode
    payload.dynamic_discount_card_mode = eventData?.dynamic_discount_card_mode
  } else if (allowRestaurantClear && eventData?.restaurant?.id) {
    payload.restaurant_id = null
  }

  if (includeRestaurantGroup) {
    payload.restaurant_group_id = eventData?.restaurant_group_id
  }

  return payload
}

const validateEvent = (eventData, { requireRestaurantGroup = false } = {}) => {
  const nextErrors = {}
  const startDatePresent = Boolean(eventData?.start_date)
  const endDatePresent = Boolean(eventData?.end_date)
  const timeSlots = eventData?.time_slots || []
  const hasMissingTimeValues = timeSlots.length === 0 || timeSlots.some((timeSlot) => (
    !timeSlot?.all_day && (!timeSlot?.start_time || !timeSlot?.end_time)
  ))
  const hasMatchingTimeValues = timeSlots.some((timeSlot) => (
    !timeSlot?.all_day && timeSlot?.start_time && timeSlot?.end_time && timeSlot.start_time === timeSlot.end_time
  ))

  if (requireRestaurantGroup && !eventData?.restaurant_group_id) {
    nextErrors.restaurant_group_id = 'Please select a restaurant group'
  }

  if (eventData?.restaurant_id && !eventData?.dynamic_discount_event_mode) {
    nextErrors.dynamic_discount_event_mode = 'Please select an event mode'
  }

  if (eventData?.restaurant_id && !eventData?.dynamic_discount_card_mode) {
    nextErrors.dynamic_discount_card_mode = 'Please select a card mode'
  }

  if (!eventData?.name?.trim()) {
    nextErrors.name = "Name can't be blank"
  }

  if (!startDatePresent) {
    nextErrors.start_date = "Start date can't be blank"
  }

  if (startDatePresent && endDatePresent && moment(eventData.end_date).isBefore(moment(eventData.start_date), 'day')) {
    nextErrors.end_date = 'End date must be after start date'
  }

  if (hasMissingTimeValues) {
    nextErrors.time_slots = 'Start and end time required unless all day'
  } else if (hasMatchingTimeValues) {
    nextErrors.time_slots = 'Start and end time cannot be same'
  }

  return nextErrors
}

export const DynamicDiscounts = () => {
  const singleRestaurantGroupAutoApplied = useRef(false)
  const [activeSection, setActiveSection] = useState('events')
  const [shouldScrollToDiscountMatrix, setShouldScrollToDiscountMatrix] = useState(false)
  const [orderBy, setOrderBy] = useState('created_at')
  const [order, setOrder] = useState('desc')
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [editDrawerOpen, setEditDrawerOpen] = useState(false)
  const [fetchedEvent, setFetchedEvent] = useState(null)
  const [loading, setLoading] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [newEvent, setNewEvent] = useState({
    name: '',
    restaurant_group_id: '',
    restaurant_id: '',
    dynamic_discount_event_mode: '',
    dynamic_discount_card_mode: '',
    start_date: '',
    end_date: '',
    time_slots: [createEmptyTimeSlot()],
  })
  const [errors, setErrors] = useState({})
  const [createRestaurantGroupSearch, setCreateRestaurantGroupSearch] = useState('')
  const [createRestaurantSearch, setCreateRestaurantSearch] = useState('')
  const [editRestaurantSearch, setEditRestaurantSearch] = useState('')
  const showNotification = useNotificationContext();
  const { ability } = useContext(AbilityContext)
  const defaultCreatePermission = strictCanScope(ability, 'update_discount_card', 'Restaurant_Group');
  const hasAllDayTimeSlot = useMemo(
    () => newEvent?.time_slots?.some((timeSlot) => timeSlot?.all_day),
    [newEvent?.time_slots]
  )
  const editHasAllDayTimeSlot = useMemo(
    () => fetchedEvent?.time_slots?.some((timeSlot) => timeSlot?.all_day),
    [fetchedEvent?.time_slots]
  )
  const [filters, setFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('')
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [createRestaurantGroupOptionsCache, setCreateRestaurantGroupOptionsCache] = useState([])
  const [createRestaurantOptionsCache, setCreateRestaurantOptionsCache] = useState([])
  const queryClient = useQueryClient();
  const [createInstancePermission, setCreateInstancePermission] = useState(false)
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const [updateErrors, setUpdateErrors] = useState({})
  const [deleteTargetEvent, setDeleteTargetEvent] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false)
  const [allDayToggleConfirmation, setAllDayToggleConfirmation] = useState(null)
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedCreateRestaurantGroupSearch = useDebounce(createRestaurantGroupSearch.trim(), 300)
  const debouncedCreateRestaurantSearch = useDebounce(createRestaurantSearch.trim(), 300)
  const debouncedEditRestaurantSearch = useDebounce(editRestaurantSearch.trim(), 300)

  const handleSectionChange = useCallback((sectionKey) => {
    setActiveSection(sectionKey)
    if (sectionKey !== 'discounts') {
      setShouldScrollToDiscountMatrix(false)
    }
  }, [])

  const handleCalendarNavigateToDiscounts = useCallback(() => {
    setActiveSection('discounts')
    setShouldScrollToDiscountMatrix(true)
  }, [])

  const fetchRestaurantGroupList = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });

    return response?.data?.map(normalizeRestaurantGroup) || [];
  }, [debouncedRestaurantGroupSearch]);

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const fetchCreateRestaurantGroupList = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/permission_restaurant_group_list', {
      params: {
        resource_action: 'update_discount_card',
        page: pageParam,
        per_page: PerPage,
        search: debouncedCreateRestaurantGroupSearch || undefined,
      }
    });
    return response?.data || [];
  }, [debouncedCreateRestaurantGroupSearch])

  const {
    data: createRestaurantGroupsList = [],
    isFetching: createRestaurantGroupFetching,
    isFetchingNextPage: createRestaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextCreateRestaurantGroupsPage,
    hasNextPage: hasNextCreateRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.permission_restaurant_group({
      resource_action: 'update_discount_card',
      search: debouncedCreateRestaurantGroupSearch,
    }),
    queryFn: fetchCreateRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: createRestaurantGroupMenuRef, targetRef: createRestaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextCreateRestaurantGroupsPage,
    hasNextPage: hasNextCreateRestaurantGroupsPage,
    isFetchingNextPage: createRestaurantGroupsFetchingNextPage,
  })

  const activeDrawerRestaurantGroupId = createDrawerOpen
    ? newEvent?.restaurant_group_id
    : fetchedEvent?.restaurant_group_id

  const activeDrawerRestaurantSearch = createDrawerOpen
    ? debouncedCreateRestaurantSearch
    : debouncedEditRestaurantSearch

  const fetchCreateRestaurantList = useCallback(async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: { 
        restaurant_group_ids: activeDrawerRestaurantGroupId,
        resource_action: 'update_discount_card',
        page: pageParam,
        per_page: PerPage,
        search: activeDrawerRestaurantSearch || undefined,
      }
    })

    return response?.data?.map((restaurant) => {
      const normalizedRestaurant = normalizeRestaurant(restaurant)

      return {
        value: String(normalizedRestaurant?.id),
        label: normalizedRestaurant?.name,
      }
    }) || []
  }, [activeDrawerRestaurantGroupId, activeDrawerRestaurantSearch])

  const {
    data: createRestaurantsList = [],
    isFetching: createRestaurantFetching,
    isFetchingNextPage: createRestaurantsFetchingNextPage,
    fetchNextPage: fetchNextCreateRestaurantsPage,
    hasNextPage: hasNextCreateRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: activeDrawerRestaurantGroupId,
      resource_action: 'update_discount_card',
      search: activeDrawerRestaurantSearch,
      source: 'dynamic_discount_form'
    }),
    queryFn: fetchCreateRestaurantList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: flattenDropdownPages,
    staleTime: 10 * 60 * 1000,
    enabled: (createDrawerOpen || editDrawerOpen) && Boolean(activeDrawerRestaurantGroupId),
    placeholderData: keepPreviousData,
  })

  const { rootRef: createRestaurantMenuRef, targetRef: createRestaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextCreateRestaurantsPage,
    hasNextPage: hasNextCreateRestaurantsPage,
    isFetchingNextPage: createRestaurantsFetchingNextPage,
  })

  const permittedCreateRestaurantGroupIds = useMemo(
    () => new Set((createRestaurantGroupOptionsCache || []).map((restaurantGroup) => String(restaurantGroup?.value))),
    [createRestaurantGroupOptionsCache]
  )

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantGroups, 'label', ['value']))
  }, [restaurantGroups])

  useEffect(() => {
    setCreateRestaurantGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, createRestaurantGroupsList, 'value', ['label']))
  }, [createRestaurantGroupsList])

  useEffect(() => {
    setCreateRestaurantOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, createRestaurantsList, 'value', ['label']))
  }, [createRestaurantsList])

  useEffect(() => {
    if (!fetchedEvent?.restaurant?.id || !fetchedEvent?.restaurant?.name) {
      return
    }

    setCreateRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, [{
      value: String(fetchedEvent.restaurant.id),
      label: fetchedEvent.restaurant.name,
    }], 'value'))
  }, [fetchedEvent?.restaurant?.id, fetchedEvent?.restaurant?.name])

  const fetchDynamicDiscountEvents = async () => {
    const response = await api.get('/v1/admin/dynamic_discount_events', {
      params: {
        per_page: itemsPerPage,
        page: page,
        restaurant_group_id: appliedFilters?.restaurant_group_id?.includes(ALL_VALUE) ? undefined : appliedFilters?.restaurant_group_id,
        scope_type: appliedFilters?.scope_type === ALL_VALUE ? undefined : appliedFilters?.scope_type,
        sort_by: orderBy,
        sort_order: order,
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: dynamicDiscountEvents, isLoading, isFetching } = useQuery({
    queryKey: Query_Keys.dynamic_discount_events({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      orderBy,
      order,
    }),
    queryFn: fetchDynamicDiscountEvents,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const handleSortChange = useCallback((nextOrderBy, nextOrder) => {
    setOrderBy(nextOrderBy)
    setOrder(nextOrder)
    setPage(1)
  }, [])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups)

  const hasExplicitRestaurantGroupSelection = useMemo(() => {
    if (!filters?.restaurant_group_id?.length) {
      return false
    }

    return !(filters.restaurant_group_id.length === 1 && filters.restaurant_group_id[0] === ALL_VALUE)
  }, [filters?.restaurant_group_id])

  const updateCreateInstancePermission = useCallback((restaurantGroupIds = []) => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setCreateInstancePermission(
        strictCanScope(ability, 'update_discount_card', 'Restaurant_Group', `${singleRestaurantGroup?.label}`)
        || permittedCreateRestaurantGroupIds.has(String(singleRestaurantGroup?.label))
      )
      return
    }

    if (!restaurantGroupIds || restaurantGroupIds.includes(ALL_VALUE) || restaurantGroupIds.length !== 1) {
      setCreateInstancePermission(false)
      return
    }

    setCreateInstancePermission(
      strictCanScope(ability, 'update_discount_card', 'Restaurant_Group', `${restaurantGroupIds[0]}`)
      || permittedCreateRestaurantGroupIds.has(String(restaurantGroupIds[0]))
    )
  }, [ability, hasSingleAvailableRestaurantGroup, permittedCreateRestaurantGroupIds, restaurantGroups])

  useEffect(() => {
    if (hasExplicitRestaurantGroupSelection || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    const singleRestaurantGroup = getSingleItemValue(restaurantGroups);

    singleRestaurantGroupAutoApplied.current = true
    setFilters(prev => ({
      ...prev,
      restaurant_group_id: [singleRestaurantGroup.label]
    }));
  }, [hasExplicitRestaurantGroupSelection, hasSingleAvailableRestaurantGroup, restaurantGroups]);

  useEffect(() => {
    updateCreateInstancePermission(filters?.restaurant_group_id)
  }, [filters?.restaurant_group_id, updateCreateInstancePermission])

  const handleDrawerClose = () => {
    setCreateDrawerOpen(false)
    setNewEvent({
      name: '',
      restaurant_group_id: '',
      restaurant_id: '',
      dynamic_discount_event_mode: '',
      dynamic_discount_card_mode: '',
      start_date: '',
      end_date: '',
      time_slots: [createEmptyTimeSlot()],
    })
    setErrors({})
    setCreateRestaurantGroupSearch('')
    setCreateRestaurantSearch('')
    setAllDayToggleConfirmation(null)
  }

  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false)
    setFetchedEvent(null)
    setUpdateErrors({})
    setEditRestaurantSearch('')
    setAllDayToggleConfirmation(null)
  }

  const handleInputChange = (value, field, mode = "create") => {
    if (mode === "create") {
      setNewEvent((prev) => {
        if (field === 'restaurant_group_id') {
          return {
            ...prev,
            restaurant_group_id: value,
            restaurant_id: '',
            dynamic_discount_event_mode: '',
            dynamic_discount_card_mode: '',
          }
        }

        if (field === 'restaurant_id') {
          return {
            ...prev,
            restaurant_id: value,
            dynamic_discount_event_mode: '',
            dynamic_discount_card_mode: '',
          }
        }

        return { ...prev, [field]: value }
      })
    } else {
      setFetchedEvent((prev) => {
        if (field === 'restaurant_id') {
          if (prev?.restaurant_id === value) {
            return prev
          }

          return {
            ...prev,
            restaurant_id: value,
            dynamic_discount_event_mode: '',
            dynamic_discount_card_mode: '',
          }
        }

        return { ...prev, [field]: value }
      })
    }
  }

  const handleDateChange = (value, field, mode = 'create') => {
    handleInputChange(value?.isValid?.() ? value.format('YYYY-MM-DD') : '', field, mode)

    if (mode === 'create') {
      setErrors(prev => ({
        ...prev,
        [field]: null,
        ...(field === 'start_date' && value?.isValid?.() ? { end_date: null } : {}),
      }))
      return
    }

    setUpdateErrors(prev => ({ ...prev, [field]: null }))
  }

  const updateTimeSlots = (mode, updater) => {
    if (mode === 'create') {
      setNewEvent((prev) => ({
        ...prev,
        time_slots: updater(prev.time_slots),
      }))
      return
    }

    setFetchedEvent((prev) => ({
      ...prev,
      time_slots: updater(prev?.time_slots || []),
    }))
  }

  const handleTimeSlotChange = (index, field, value, mode = 'create') => {
    updateTimeSlots(mode, (timeSlots) => timeSlots.map((timeSlot, timeSlotIndex) => (
      timeSlotIndex === index ? { ...timeSlot, [field]: value } : timeSlot
    )))

    if (mode === 'create') {
      setErrors(prev => ({ ...prev, time_slots: null }))
      return
    }

    setUpdateErrors(prev => ({ ...prev, time_slots: null }))
  }

  const handleAddTimeSlot = (mode = 'create') => {
    updateTimeSlots(mode, (timeSlots) => {
      if (timeSlots.some((timeSlot) => timeSlot.all_day)) {
        return timeSlots
      }

      return [...timeSlots, createEmptyTimeSlot()]
    })

    if (mode === 'create') {
      setErrors(prev => ({ ...prev, time_slots: null }))
      return
    }

    setUpdateErrors(prev => ({ ...prev, time_slots: null }))
  }

  const handleDeleteTimeSlot = (index, mode = 'create') => {
    updateTimeSlots(mode, (timeSlots) => timeSlots.filter((_, timeSlotIndex) => timeSlotIndex !== index))

    if (mode === 'create') {
      setErrors(prev => ({ ...prev, time_slots: null }))
      return
    }

    setUpdateErrors(prev => ({ ...prev, time_slots: null }))
  }

  const applyAllDayToggle = (index, checked, mode = 'create') => {
    updateTimeSlots(mode, (timeSlots) => (
      checked
        ? [{ ...timeSlots[index], ...allDayDisplayTimeRange, all_day: true }]
        : timeSlots.map((timeSlot, timeSlotIndex) => {
          if (timeSlotIndex !== index) {
            return timeSlot
          }

          return { ...timeSlot, start_time: '', end_time: '', all_day: false }
        })
    ))

    if (mode === 'create') {
      setErrors(prev => ({ ...prev, time_slots: null }))
      return
    }

    setUpdateErrors(prev => ({ ...prev, time_slots: null }))
  }

  const handleAllDayToggle = (index, checked, mode = 'create') => {
    const timeSlots = mode === 'create' ? newEvent?.time_slots || [] : fetchedEvent?.time_slots || []

    if (checked && timeSlots.length > 1) {
      setAllDayToggleConfirmation({
        index,
        checked,
        mode,
        removedSlotsCount: timeSlots.length - 1,
      })
      return
    }

    applyAllDayToggle(index, checked, mode)
  }

  const handleAllDayToggleConfirm = () => {
    if (!allDayToggleConfirmation) {
      return
    }

    const { index, checked, mode } = allDayToggleConfirmation

    setAllDayToggleConfirmation(null)
    applyAllDayToggle(index, checked, mode)
  }

  const getCreateRestaurantGroupName = (id) => {
    const restaurantGroup = createRestaurantGroupOptionsCache?.find((res) => String(res.value) === String(id))
    return restaurantGroup ? restaurantGroup?.label : ''
  }

  const getCreateRestaurantName = (id) => {
    if (!id) {
      return ''
    }

    const normalizedId = String(id)
    const restaurant = createRestaurantOptionsCache?.find((res) => String(res.value) === normalizedId)

    if (restaurant) {
      return restaurant?.label
    }

    return String(fetchedEvent?.restaurant?.id) === normalizedId ? fetchedEvent?.restaurant?.name || '' : ''
  }

  const displayedRestaurantGroups = useMemo(() => {
    if (debouncedRestaurantGroupSearch || !filters?.restaurant_group_id?.length) {
      return restaurantGroups
    }

    const selectedRestaurantGroups = restaurantGroupOptionsCache.filter((restaurantGroup) => (
      filters.restaurant_group_id.includes(restaurantGroup?.label)
    ))

    return mergeUniqueByKey(restaurantGroups, selectedRestaurantGroups, 'label')
  }, [debouncedRestaurantGroupSearch, filters?.restaurant_group_id, restaurantGroupOptionsCache, restaurantGroups])

  const editSelectedRestaurantOption = useMemo(() => {
    if (!fetchedEvent?.restaurant?.id || !fetchedEvent?.restaurant?.name) {
      return null
    }

    return {
      value: String(fetchedEvent.restaurant.id),
      label: fetchedEvent.restaurant.name,
    }
  }, [fetchedEvent?.restaurant?.id, fetchedEvent?.restaurant?.name])

  const displayedEditRestaurants = useMemo(() => {
    if (!editSelectedRestaurantOption || debouncedEditRestaurantSearch) {
      return createRestaurantsList
    }

    return mergeUniqueByKey(createRestaurantsList, [editSelectedRestaurantOption], 'value')
  }, [createRestaurantsList, debouncedEditRestaurantSearch, editSelectedRestaurantOption])

  const getRestaurantGroupDisplayValue = () => {
    if (!filters?.restaurant_group_id || filters?.restaurant_group_id?.length === 0) {
      return 'All';
    }

    if (filters?.restaurant_group_id?.includes(ALL_VALUE)) {
      return 'All';
    }

    if (filters?.restaurant_group_id?.length === 1) {
      const restaurantGroup = restaurantGroupOptionsCache.find((currentRestaurantGroup) => currentRestaurantGroup.label === filters.restaurant_group_id[0]);
      return restaurantGroup ? restaurantGroup.value : 'All';
    }

    return `${filters?.restaurant_group_id?.length} Restaurant Groups Selected`;
  }

  const getScopeDisplayValue = () => {
    return scopeFilterOptions.find((option) => option.value === (filters?.scope_type || ALL_VALUE))?.label || ALL_VALUE
  }

  const handleRestaurantGroupChange = (event) => {
    const {
      target: { value },
    } = event;
    setFiltersChanged(true);

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters({ ...filters, restaurant_group_id: [ALL_VALUE], restaurant_id: [ALL_VALUE] });
    }
    else {
      const newRestaurantGroupIds = value.filter(val => val !== ALL_VALUE);
      setFilters({ ...filters, restaurant_group_id: newRestaurantGroupIds, restaurant_id: [ALL_VALUE] });
    }
  }

  const handleRestaurantGroupClear = () => {
    setFilters({ ...filters, restaurant_group_id: [ALL_VALUE] })
    setFiltersChanged(true)
  }

  const handleScopeChange = (event) => {
    const {
      target: { value },
    } = event;

    setFilters({ ...filters, scope_type: value || ALL_VALUE })
    setFiltersChanged(true)
  }

  const handleScopeClear = () => {
    setFilters({ ...filters, scope_type: ALL_VALUE })
    setFiltersChanged(true)
  }

  const handleUpdate = () => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setPage(1)
    setItemsPerPage(25)
  }

  const handleReset = () => {
    setFiltersChanged(false)
    setFilters(defaultFilters)
    setAppliedFilters(defaultFilters)
    setItemsPerPage(25);
    setPage(1);
  }

  const formattedExportFilters = useMemo(() => ({
    // Keep export filters aligned with the page-level Events filters.
    restaurant_group_id: appliedFilters?.restaurant_group_id?.includes(ALL_VALUE)
      ? undefined
      : appliedFilters?.restaurant_group_id,
    scope_type: appliedFilters?.scope_type === ALL_VALUE ? undefined : appliedFilters?.scope_type,
  }), [appliedFilters])

  const handleExportCsv = async () => {
    if (csvLoading) return

    setCsvLoading(true)

    try {
      const query = buildQueryParams(formattedExportFilters)
      const { response } = await authenticatedFetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=dynamic_discount_events&${query}`,
        {
          method: 'GET',
          defaultErrorMessage: 'Failed to download CSV',
        }
      )

      const blob = await response.blob()

      // This prevents downloading a network-returned empty body (0 KB).
      // It does not block headers-only CSV responses, which still have non-zero size.
      if (!blob || blob.size === 0) {
        throw new Error('Failed to download CSV')
      }

      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', `dynamic_discount_events_${dayjs().format('YYYY-MM-DD')}.csv`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      showNotification('CSV downloaded successfully', 'success')
    } catch (error) {
      showNotification(error?.message || 'Failed to download CSV', 'error')
    } finally {
      setCsvLoading(false)
    }
  }

  const { mutateAsync: createDynamicDiscountEventMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post("/v1/admin/dynamic_discount_events", payload),
    invalidateKeys: [...dynamicDiscountMutationKeyGroups.create],
    successMessage: () => "Event has been created successfully",
    errorMessage: ({ error }) => error?.response?.data?.errors?.base?.[0] || null,
    onSuccess: () => {
      handleDrawerClose();
    },
    onError: (error) => {
      setErrors(error?.response?.data?.errors || {});
    },
    onSettled: () => setLoading(false)
  });

  const handleCreate = async () => {
    const validationErrors = validateEvent(newEvent, { requireRestaurantGroup: true })

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    const payload = buildEventPayload(newEvent, { includeRestaurantGroup: true })

    setLoading(true);

    try {
      await createDynamicDiscountEventMutation(payload);
    } catch {
      // Error handled in onError callback
    }
  }

  const handleEditEvent = (record) => {
    setFetchedEvent({
      ...record,
      restaurant_group_id: record?.restaurant_group?.id ? String(record.restaurant_group.id) : '',
      restaurant_id: record?.restaurant?.id ? String(record.restaurant.id) : '',
      time_slots: (record?.time_slots || []).map((timeSlot) => ({
        ...timeSlot,
        start_time: formatTimeForForm(timeSlot?.start_time),
        end_time: formatTimeForForm(timeSlot?.end_time),
      }))
    })
    setUpdateErrors({})
    setEditRestaurantSearch('')
    setEditDrawerOpen(true)
  }

  const { mutateAsync: updateDynamicDiscountEventMutation } = useOtMutation({
    mutationFn: async (payload) => await api.put(`/v1/admin/dynamic_discount_events/${fetchedEvent.id}`, payload),
    invalidateKeys: [...dynamicDiscountMutationKeyGroups.update],
    successMessage: () => 'Event has been updated successfully',
    errorMessage: ({ error }) => error?.response?.data?.errors?.base?.[0] || null,
    onSuccess: () => {
      handleEditDrawerClose()
    },
    onError: (error) => {
      setUpdateErrors(error?.response?.data?.errors || {})
    },
    onSettled: () => setLoading(false)
  });

  const handleUpdateEvent = async () => {
    if (!fetchedEvent?.id) {
      return
    }

    const validationErrors = validateEvent(fetchedEvent)

    if (Object.keys(validationErrors).length > 0) {
      setUpdateErrors(validationErrors)
      return
    }

    const payload = buildEventPayload(fetchedEvent, { allowRestaurantClear: true })

    setLoading(true)

    try {
      await updateDynamicDiscountEventMutation(payload);
    } catch {
      // Handled in onError
    }
  }

  const { mutateAsync: deleteDynamicDiscountEventMutation } = useOtMutation({
    mutationFn: async () => await api.delete(`/v1/admin/dynamic_discount_events/${deleteTargetEvent?.id}`),
    invalidateKeys: [...dynamicDiscountMutationKeyGroups.delete],
    successMessage: () => "Event has been deleted successfully",
    errorMessage: ({ error }) => {
      const errors = error?.response?.data?.errors;
      return errors?.base?.[0] || errors?.event_id?.[0] || 'Failed to delete event';
    },
    onSuccess: () => {
      setDeleteEventDialogOpen(false)
      if (deleteTargetEvent?.id === fetchedEvent?.id && editDrawerOpen) {
        handleEditDrawerClose();
      }

      resetPageAfterDeletion({ totalCount: dynamicDiscountEvents?.pageCount, removed: 1, page, itemsPerPage, setPage })

      setDeleteLoading(false);
    },
    onError: () => {
      setDeleteEventDialogOpen(false);
      setDeleteLoading(false);
    },
    onSettled: () => {
      setDeleteLoading(false);
    }
  });

  const handleDeleteEvent = async () => {
    setDeleteLoading(true);

    try {
      await deleteDynamicDiscountEventMutation();
    } catch {
      // Handled in onError
    }
  }

  const handleDeleteEventClick = (record) => {
    setDeleteTargetEvent(record);
    setDeleteEventDialogOpen(true);
  }

  const getPermission = (action, record) => {
    if (superAdmin?.role === 'admin') {
      return true
    }

    return strictCanScope(ability, action, 'Restaurant_Group', `${record?.restaurant_group?.id}`)
  }

  const TableColumns = [
    { key: "id", sortable: true },
    { key: "event_name", sortable: true },
    { key: "restaurant_group", sortable: true },
    { key: "scope", sortable: true },
    { key: "restaurant", sortable: true },
    { key: "start_date", sortable: true },
    { key: "end_date", sortable: true },
    { key: "time_windows", sortable: true },
    { key: "actions", sortable: false },
  ]

  const columns = TableColumns?.map((column) => {
    return {
      key: column.key,
      label: camelize(column.key),
      sortable: column.sortable,
    }
  })

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        event_name: record?.name,
        restaurant_group: record?.restaurant_group?.name,
        scope: formatEventScopeLabel(record?.scope_type),
        restaurant: record?.restaurant?.name || '-',
        start_date: moment.utc(record?.start_date).format('MM/DD/YYYY'),
        end_date: moment.utc(record?.end_date).format('MM/DD/YYYY'),
        time_windows: record?.time_slots?.length,
        actions: (
          <React.Fragment>
            <Tooltip title="Edit Event" arrow>
              <span>
                <IconButton
                  disabled={!getPermission('update_discount_card', record)}
                  onClick={() => handleEditEvent(record)}
                  data-testid={`edit-event-button-${record?.id}`}
                >
                  <ModeEditIcon
                    sx={{
                      color: getPermission('update_discount_card', record) ? "#707070" : "#e0e0e0",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete Event" arrow >
              <span>
                <IconButton
                  disabled={!getPermission('update_discount_card', record)}
                  onClick={() => handleDeleteEventClick(record)}
                  data-testid={`delete-event-button-${record?.id}`}
                >
                  <DeleteIcon
                    sx={{
                      color: getPermission('update_discount_card', record) ? "#707070" : "#e0e0e0",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </React.Fragment>
        )
      }
    })
  }

  const rows = TableRows(dynamicDiscountEvents?.data);

  return (
    <RestaurantGroupContainer
      style={{
        width: createDrawerOpen || editDrawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%'
      }}
    >
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <StyledLink color={'#515152'}>
            Dynamic Perqs
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <FilterContainer>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              gap: "18px",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              width: { xs: "100%", md: "auto" }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                ...viewTypographyStyles,
                alignSelf: { xs: "flex-start", sm: "center" },
                whiteSpace: "nowrap",
                mt: 0
              }}
            >
              View
            </Typography>

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <StyledInputLabel>Restaurant Group</StyledInputLabel>
              <Select
                size="small"
                label="Restaurant Group"
                data-testid="events-restaurant-group-filter"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                  autoFocus: false,
                  MenuListProps: { sx: { pt: 0 } },
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: 160,
                  borderColor: "#2b4fb9",
                }}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                value={hasSingleAvailableRestaurantGroup
                  ? [getSingleItemValue(restaurantGroups)?.label]
                  : (filters?.restaurant_group_id || [ALL_VALUE])}
                multiple
                renderValue={() => {
                  if (hasSingleAvailableRestaurantGroup) {
                    return getSingleItemValue(restaurantGroups)?.value || [ALL_VALUE];
                  }
                  return getRestaurantGroupDisplayValue();
                }}
                endAdornment={
                  !filters?.restaurant_group_id?.includes(ALL_VALUE) && !hasSingleAvailableRestaurantGroup ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                {/* All option */}
                {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                  key={ALL_VALUE}
                  value={ALL_VALUE}
                  disabled={!filters?.restaurant_group_id?.includes(ALL_VALUE)}
                >
                  {filters?.restaurant_group_id?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                </StyledMenuItem>
                )}
                {
                  displayedRestaurantGroups
                    ?.map((res) => (
                      <StyledMenuItem
                        key={res?.label}
                        value={res?.label}
                      >
                        {
                          hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id?.includes(res?.label) ? <SelectedMenuItem data={res?.value} /> : res?.value
                        }
                      </StyledMenuItem>
                    ))
                }
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <StyledInputLabel>Scope</StyledInputLabel>
              <Select
                size="small"
                label="Scope"
                data-testid="dynamic-discounts-scope-filter"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: { sx: { maxHeight: "40vh" } },
                  autoFocus: false,
                }}
                sx={{
                  ...selectBaseStyles,
                  minWidth: 180,
                  borderColor: "#2b4fb9",
                }}
                onChange={handleScopeChange}
                value={filters?.scope_type || ALL_VALUE}
                renderValue={() => getScopeDisplayValue()}
                endAdornment={
                  filters?.scope_type !== ALL_VALUE ? (
                    <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                      <IconButton
                        edge='end'
                        size="small"
                        disableRipple
                        onClick={handleScopeClear}
                      >
                        <ClearIcon sx={{ fontSize: '20px' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    null
                  )
                }
              >
                {scopeFilterOptions.map((option) => (
                  <StyledMenuItem
                    key={option.value}
                    value={option.value}
                  >
                    {filters?.scope_type === option.value ? <SelectedMenuItem data={option.label} /> : option.label}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              alignSelf: { xs: "flex-start", md: "center" },
              mt: { xs: 1, md: 0 },
              width: { xs: "100%", md: "auto" },
              justifyContent: { xs: "flex-start", md: "flex-end" }
            }}
          >
            <ResetButton
              onClick={handleReset}
              status={isFetching}
            />

            <UpdateButton
              onClick={handleUpdate}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </FilterContainer>

      <StyledBox report={false}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: '100%'
          }}
        >
          <StyledTitleContainer>
            <StyledTitle>Dynamic Perqs</StyledTitle>
          </StyledTitleContainer>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#ffffff',
              border: '1px solid #ececec',
              borderRadius: '14px',
              padding: '4px',
              boxShadow: '0 10px 30px rgba(17, 24, 39, 0.05)'
            }}
          >
            {dynamicDiscountSections.map((section) => {
              const isActive = activeSection === section.key

              return (
                <Box
                  key={section.key}
                  component="button"
                  type="button"
                  onClick={() => handleSectionChange(section.key)}
                  data-testid={`dynamic-discounts-section-${section.key}`}
                  sx={{
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '10px',
                    padding: '8px 28px',
                    fontSize: '14px',
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                    color: isActive ? '#f97316' : '#18181B',
                    backgroundColor: isActive ? '#fff7ed' : 'transparent',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: isActive ? '#fff7ed' : '#f9fafb'
                    }
                  }}
                >
                  {section.label}
                </Box>
              )
            })}
          </Box>
        </Box>
      </StyledBox>

      <Divider style={{ borderStyle: 'solid', borderWidth: '1px' }} />

      {activeSection === 'events' ? (
        <React.Fragment>
          <StyledBox report={false}>
            <StyledTitleContainer>
              <StyledTitle style={{ fontSize: '20px' }}>Events</StyledTitle>
              <StyledSubTitle>Create events like holidays, special days, etc...</StyledSubTitle>
            </StyledTitleContainer>
          </StyledBox>

          <div>
            <StyledCreateButton
              sx={{ marginBottom: '10px' }}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDrawerOpen(true)}
              disabled={!(defaultCreatePermission || createInstancePermission)}
              data-testid="open-event-create-drawer"
            >
              Create Event
            </StyledCreateButton>
          </div>
        </React.Fragment>
      ) : activeSection === 'discounts' ? (
        <DiscountsSection
          autoScrollToMatrix={shouldScrollToDiscountMatrix}
          onAutoScrollComplete={() => setShouldScrollToDiscountMatrix(false)}
          restaurantGroupIds={
            appliedFilters?.restaurant_group_id?.includes(ALL_VALUE)
              ? []
              : appliedFilters?.restaurant_group_id || []
          }
          scopeType={appliedFilters?.scope_type === ALL_VALUE ? undefined : appliedFilters?.scope_type}
        />
      ) : (
        <CalendarSection
          onNavigateToDiscounts={handleCalendarNavigateToDiscounts}
          restaurantGroupIds={
            appliedFilters?.restaurant_group_id?.includes(ALL_VALUE)
              ? []
              : appliedFilters?.restaurant_group_id || []
          }
          scopeType={appliedFilters?.scope_type === ALL_VALUE ? undefined : appliedFilters?.scope_type}
        />
      )}

      {createDrawerOpen && (
        <EventFormDrawer
          open={createDrawerOpen}
          mode="create"
          title="Create an Event"
          drawerWidth={drawerWidth}
          eventData={newEvent}
          errors={errors}
          loading={loading}
          timeOptions={timeOptions}
          hasAllDayTimeSlot={hasAllDayTimeSlot}
          createRestaurantGroupsList={createRestaurantGroupsList}
          createRestaurantGroupSearch={createRestaurantGroupSearch}
          setCreateRestaurantGroupSearch={setCreateRestaurantGroupSearch}
          getCreateRestaurantGroupName={getCreateRestaurantGroupName}
          createRestaurantGroupMenuRef={createRestaurantGroupMenuRef}
          createRestaurantGroupLoaderRef={createRestaurantGroupLoaderRef}
          createRestaurantGroupsHasNextPage={hasNextCreateRestaurantGroupsPage}
          createRestaurantGroupsFetchingNextPage={createRestaurantGroupsFetchingNextPage}
          createRestaurantGroupFetching={createRestaurantGroupFetching}
          createRestaurantsList={createRestaurantsList}
          createRestaurantSearch={createRestaurantSearch}
          setCreateRestaurantSearch={setCreateRestaurantSearch}
          getCreateRestaurantName={getCreateRestaurantName}
          createRestaurantMenuRef={createRestaurantMenuRef}
          createRestaurantLoaderRef={createRestaurantLoaderRef}
          createRestaurantsHasNextPage={hasNextCreateRestaurantsPage}
          createRestaurantsFetchingNextPage={createRestaurantsFetchingNextPage}
          createRestaurantFetching={createRestaurantFetching}
          dynamicDiscountModeOptions={dynamicDiscountModeOptions}
          onFieldChange={(value, field) => {
            handleInputChange(value, field)
            setErrors(prev => ({ ...prev, [field]: null }))
          }}
          onDateChange={handleDateChange}
          onAllDayToggle={(index, checked) => handleAllDayToggle(index, checked)}
          onTimeSlotChange={(index, field, value) => handleTimeSlotChange(index, field, value)}
          onAddTimeSlot={() => handleAddTimeSlot()}
          onDeleteTimeSlot={(index) => handleDeleteTimeSlot(index)}
          onClose={handleDrawerClose}
          onSubmit={handleCreate}
        />
      )}

      {editDrawerOpen && (
        <EventFormDrawer
          open={editDrawerOpen}
          mode="edit"
          title="Edit Event"
          drawerWidth={drawerWidth}
          eventData={fetchedEvent}
          errors={updateErrors}
          loading={loading}
          timeOptions={timeOptions}
          hasAllDayTimeSlot={editHasAllDayTimeSlot}
          createRestaurantGroupsList={createRestaurantGroupsList}
          createRestaurantGroupSearch={createRestaurantGroupSearch}
          setCreateRestaurantGroupSearch={setCreateRestaurantGroupSearch}
          getCreateRestaurantGroupName={getCreateRestaurantGroupName}
          createRestaurantGroupMenuRef={createRestaurantGroupMenuRef}
          createRestaurantGroupLoaderRef={createRestaurantGroupLoaderRef}
          createRestaurantGroupsHasNextPage={hasNextCreateRestaurantGroupsPage}
          createRestaurantGroupsFetchingNextPage={createRestaurantGroupsFetchingNextPage}
          createRestaurantGroupFetching={createRestaurantGroupFetching}
          createRestaurantsList={displayedEditRestaurants}
          createRestaurantSearch={editRestaurantSearch}
          setCreateRestaurantSearch={setEditRestaurantSearch}
          getCreateRestaurantName={getCreateRestaurantName}
          createRestaurantMenuRef={createRestaurantMenuRef}
          createRestaurantLoaderRef={createRestaurantLoaderRef}
          createRestaurantsHasNextPage={hasNextCreateRestaurantsPage}
          createRestaurantsFetchingNextPage={createRestaurantsFetchingNextPage}
          createRestaurantFetching={createRestaurantFetching}
          dynamicDiscountModeOptions={dynamicDiscountModeOptions}
          onFieldChange={(value, field) => {
            handleInputChange(value, field, 'edit')
            setUpdateErrors(prev => ({
              ...prev,
              [field]: null,
              ...(field === 'restaurant_id'
                ? {
                    dynamic_discount_event_mode: null,
                    dynamic_discount_card_mode: null,
                  }
                : {}),
            }))
          }}
          onDateChange={(value, field) => handleDateChange(value, field, 'edit')}
          onAllDayToggle={(index, checked) => handleAllDayToggle(index, checked, 'edit')}
          onTimeSlotChange={(index, field, value) => handleTimeSlotChange(index, field, value, 'edit')}
          onAddTimeSlot={() => handleAddTimeSlot('edit')}
          onDeleteTimeSlot={(index) => handleDeleteTimeSlot(index, 'edit')}
          onClose={handleEditDrawerClose}
          onSubmit={handleUpdateEvent}
        />
      )}

      {activeSection === 'events' && (
        (isFetching || isLoading) ? (
          <Box data-testid="loader" >
            <Loader />
          </Box>
        ) : <IndexTable
          columns={columns}
          data={rows}
          tableId={"events"}
          type="entries"
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={dynamicDiscountEvents?.pageCount}
          report={false}
          showDownloadButton={true}
          disableDownloadButton={rows?.length === 0 || csvLoading}
          handleExportCsv={handleExportCsv}
          csvLoading={csvLoading}
          sortMode="server"
          sortBy={orderBy}
          sortOrder={order}
          onSortChange={handleSortChange}
        />
      )}

      {activeSection === 'events' && (
        <ConfirmationDialog
          open={deleteEventDialogOpen}
          onClose={() => setDeleteEventDialogOpen(false)}
          Icon={CheckIcon}
          dialogTitle="Are you sure you want to delete the event?"
          dialogBody={`This action will remove event: ${deleteTargetEvent?.name
            ? `${deleteTargetEvent?.name}`
            : "Unnamed Event"
            } from restaurant group: ${deleteTargetEvent?.restaurant_group?.name}`}
          confirmationText="Yes, I want to delete the event"
          handleConfirm={handleDeleteEvent}
          isLoading={deleteLoading}
        />
      )}

      <ConfirmationDialog
        open={Boolean(allDayToggleConfirmation)}
        onClose={() => setAllDayToggleConfirmation(null)}
        Icon={CheckIcon}
        dialogTitle="Replace other time slots with an all-day slot?"
        dialogBody={allDayToggleConfirmation
          ? `Marking this slot as all day will remove the other ${allDayToggleConfirmation.removedSlotsCount} configured time slot${allDayToggleConfirmation.removedSlotsCount === 1 ? '' : 's'} for this event.`
          : ''}
        confirmationText="Yes, replace the other time slots"
        handleConfirm={handleAllDayToggleConfirm}
        dataTestId="all-day-toggle-confirmation-dialog"
      />

    </RestaurantGroupContainer>
  );
}
